import { ChangeDetectionStrategy, Component, ComponentRef, ViewChild, inject, ViewContainerRef, OnInit, signal } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { ClientStatsSectionFactoryService } from '../../services/stats/client-stats-section-factory.service';
import { ConfirmationService } from "primeng/api";
import { SharedService } from "../../../shared/services/shared.service";
import { BrandService } from '../../../services/config/brand.service';
import { TokenStorageService } from '../../../services/safeguard/token-storage.service';
import { ClientStatsService } from "../../services/stats/client-stats.service";
import { CustomLoggerService } from '../../../services/monitor/custom-logger.service';
import { IBrand } from '../../../shared/interfaces/brand';
import { BrandAdminHeaderComponent } from '../../../shared/components/brand-admin-header/brand-admin-header.component';

type ChartKind = 'bar' | 'line' | 'pie' ;

interface TileStat {
  key: string;
  icon: string;       // icon name from Material Icons
  title: string;
  value?: number | string;
  delta?: number;        // % change vs prior period
  subtitle?: string;
  description?: string;  // used in the details panel
}

@Component({
  selector: 'app-client-dashboard-tiles',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // imports: [],
  templateUrl: './client-dashboard-tiles.component.html',
  styleUrl: './client-dashboard-tiles.component.css'
})

export class ClientDashboardTilesComponent implements OnInit{
  tiles: TileStat[] = [
    { 
      key: 'enrollments', 
      icon: 'done_outline',    
      title: 'Enrollments',         
      // value: 10, 
      // delta: 3.8,  
      // subtitle: 'Last 24h', 
      // description: 'Number of enrollments in the last 24 hours.' 
    },
    { 
      key: 'unenrollments',  
      icon: 'remove_circle_outline',
      title: 'Unenrollments',      
      value: 5, 
      delta: 1.2,  
      subtitle: 'Last 1 day', 
      description: 'Number of unenrollments in the last 24 hours.' 
    },
    { 
      key: 'notifications',   
      icon: 'message',   
      title: 'Notifications Sent',    
      value: 243, 
      delta: -0.2, 
      subtitle: 'Last 1 day', 
      description: 'Number of notifications sent in the last 24 hours.' 
    },
    { 
      key: 'pref-changes',   
      icon: 'settings',
      title: 'Preference Changes',       
    },
    { 
      key: 'clickthroughs',    
      icon: 'link',
      title: 'Clickthroughs',        
    },
    { 
      key: 'incomplete-enrollments',
      icon: 'gps_off',  
      title: 'Incomplete Enrollments',        
    },
  ];

  titleMessage = "Client Dashboard";
  tileSubTitle = "Last 24 hours";

  openBrandSelectionDialog: boolean = false;
  isBrandSelectionButtonShown: boolean = false;
  isSpinnerHidden: boolean = true;
  isDashboardHidden: boolean = false;
  isDetailsShown = false;

  currentBrand!: IBrand;
  clientBrandCode!: string;
  brandName!: string;
  selectedBrandName!: string;
  selectedIndex: number | null = null;
  selectedBrand!: string;
  baseUrl!: string;

  selectedDateFrom: string | undefined;
  selectedDateTo: string | undefined; 

  isSamsungPlatformEnabled: boolean = false;

  chartTypes: { label: string; value: ChartKind }[] = [
    { label: 'Bar', value: 'bar' },
    // { label: 'Doughnut', value: 'doughnut' },
    { label: 'Line', value: 'line' },
    { label: 'Pie', value: 'pie' },
  ];
  selectedChartType: 'bar' | 'line' | 'pie' = 'bar';  

  @ViewChild(BrandAdminHeaderComponent, {static : true}) child! : BrandAdminHeaderComponent; 

  private _pendingIndex: number | null = null;
  private _clientStatsVcr: ViewContainerRef | undefined;
  private _activeCmpRef: ComponentRef<any> | null = null;

  @ViewChild('clientStatsAnchor', { read: ViewContainerRef, static: false })
  set clientStatsVcr(vcr: ViewContainerRef | undefined) {
    this._clientStatsVcr = vcr;

    // If anchor just appeared and we queued a load, do it now
    if (vcr && this._pendingIndex != null) {
      this.loadDetailsForIndex(this._pendingIndex);
      this._pendingIndex = null;
    }
  }

  // Parent-controlled string signals
  private readonly _subcomponetMode = signal<string>('Summary');
  private readonly _chartType = signal<string>('bar');

  get subcomponetMode() {
    return this._subcomponetMode();
  }

  get chartType() {
    return this._chartType();
  }

  // Call this to notify current child
  sendSubcomponetMode(msg: string) {
    this._subcomponetMode.set(msg);
  }

  sendChartType(chartType: string) {
    this._chartType.set(chartType);
  }

  public readonly sharedService = inject(SharedService);
  public readonly tokenStorageService = inject(TokenStorageService);
  public readonly clientStatsSectionFactoryService = inject(ClientStatsSectionFactoryService);
  public readonly brandService = inject(BrandService);
  public readonly clientStatsService = inject(ClientStatsService);
  public readonly customLoggerService = inject(CustomLoggerService);
  public readonly confirmationService = inject(ConfirmationService);

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Prepopulate date from and to
    this.selectedDateFrom = this.sharedService.getPreviousDate(1);
    this.selectedDateTo = this.sharedService.getPreviousDate(1);    
    // Figure out whether the user has just one granted brand - in this case we can eliminate brand selection altogether
    let assignedBrand = this.tokenStorageService.getCurrentUserAssignedBrand();
    if (assignedBrand) {
      this.setSingleBrand(assignedBrand);
    } else {      
      this.isBrandSelectionButtonShown = true;
      this.currentBrand = this.sharedService.getCurrentBrand();
      if (this.currentBrand && this.currentBrand.brandCode) {
        this.child.makeTitleMessage(this.currentBrand, this.titleMessage);
        this.selectedBrand = this.currentBrand.brandCode || '';
        this.baseUrl = this.currentBrand.baseUrl || '';
        this.brandName = this.currentBrand.brandName || '';
        this.clientBrandCode = this.selectedBrand;
        this.getDashboardData();
        this.openBrandSelectionDialog = false;
      } else {
        this.openBrandSelectionDialog = true;
      } 
    }
  }

    // Set single brand as the default when the user has just one brand granted
  setSingleBrand(brandCode: string) {
    this.isBrandSelectionButtonShown = false;
    this.isSpinnerHidden = false;
    this.brandService.getBrandWithRegion(brandCode)
    .subscribe (brand => {
      this.currentBrand = brand;
      this.child.makeTitleMessage(this.currentBrand, this.titleMessage);
      this.baseUrl = brand.baseUrl || '';
      this.selectedBrand = brand.brandCode || '';
      this.clientBrandCode = brand.brandCode || '';
      this.brandName = this.currentBrand.brandName || '';
      this.isSpinnerHidden = true;
      this.getDashboardData();
    });
  }

  // When the user opens up the dialog to select a brand 
  onChooseBrandClick(event: any) {
    this.openBrandSelectionDialog = true;
  }

  // When a brand is chosen
  chooseBrand(item: IBrand) {
    this.openBrandSelectionDialog = false;
    if (item != null) {
      this.currentBrand = item;
      this.getBrandProperties(item);
      this.sharedService.setCurrentBrand(item);
      this.selectedBrand = item.brandCode || '';
      this.selectedBrandName = " ( " + item.brandName + " )";
      this.baseUrl = item.baseUrl || '';
      this.brandName = item.brandName || '';
      this.child.makeTitleMessage(this.currentBrand, this.titleMessage);
      this.getDashboardData();
    }
  }

  // Find out brand properies from the IBrand object
  getBrandProperties(brand: IBrand) {
    // console.log('homeBiller: ', 'inside getBrandProperties');
    this.brandName = brand.brandName || '';
    this.clientBrandCode = brand.brandCode || '';
    // console.log('clientBrandCode: ', this.clientBrandCode);
    this.baseUrl = brand.baseUrl || '';
  }

  getDashboardData() {
    if (!(this.baseUrl && this.selectedBrand && this.selectedDateFrom && this.selectedDateTo)) {
      this.confirmationService.confirm({
        message: 'Make sure to select a brand and the date interval in the selection criteria to retireve database records.',
        header: 'Warning',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: "Ok",
        rejectVisible: false,
        acceptButtonStyleClass: "p-button-info wal-info  p-button-rounded",
        accept: () => {
              return;
            }
      });          
      return;
    }
    // Calculate the date interval from the selection criteria
    const days = this.sharedService.daysBetween(this.selectedDateFrom, 
      this.sharedService.getNextDate(this.selectedDateTo));
    if (days == 1) {
      this.tileSubTitle = "Last 1 day";
    } else {
      this.tileSubTitle = `Last ${days} days`;
    }    

    this.getEnrollmentsStats(this.baseUrl, this.selectedBrand, this.selectedDateFrom, this.selectedDateTo);
    this.getUnEnrollmentsStats(this.baseUrl, this.selectedBrand, this.selectedDateFrom, this.selectedDateTo);
    this.getNotificationsStats(this.baseUrl, this.selectedBrand, this.selectedDateFrom, this.selectedDateTo);
    this.getClickthroughsStats(this.baseUrl, this.selectedBrand, this.selectedDateFrom, this.selectedDateTo);
    this.getPrefChangesStats(this.baseUrl, this.selectedBrand, this.selectedDateFrom, this.selectedDateTo);
    this.getIncompleteEnrollmentsStats(this.baseUrl, this.selectedBrand, this.selectedDateFrom, this.selectedDateTo);

    if (this._activeCmpRef) {
      this._activeCmpRef.setInput('params', {
        brandCode: this.selectedBrand,
        brandName: this.brandName,
        baseUrl: this.baseUrl,
        isSamsungPlatformEnabled: this.isSamsungPlatformEnabled,
        dateFrom: this.selectedDateFrom,
        dateTo: this.selectedDateTo
      });
    }
  }

  // When the user clicks on the Search button of the selection criteria
  onSearchClick(event: any) {
    this.getDashboardData();
  }

  // Clear the selection criteria
  onClearClick(event: any) {
    this.selectedDateFrom = '';
    this.selectedDateTo = '';
  }  

  // Retrieve enrollments stats for the given date interval, then display them in the panel
  getEnrollmentsStats(baseUrl: string, selectedBrand: string, selectedDateFrom: string, selectedDateTo: string) {
        const idx = this.tiles.findIndex(t => t.key === 'enrollments');
        if (idx !== -1) {
          const updated: TileStat = {
            ...this.tiles[idx],
            value: 10,
            delta: 3.8,
            subtitle: this.tileSubTitle,
            key: 'enrollments',    
            icon: 'done_outline',
            title: 'Enrollments',        
            description: 'Number of enrollments in the ' + this.tileSubTitle.toLocaleLowerCase() + '.' 
          };

          // ❗ Replace array reference (immutable update)
          this.tiles = [
            ...this.tiles.slice(0, idx),
            updated,
            ...this.tiles.slice(idx + 1)
          ];
        }
        this.customLoggerService.logInfo('Enrollments stats tiles: ', this.tiles);

        this.cdr.markForCheck(); // cause the tile values to repaint
  }

  // Retrieve unenrollments stats for the given date interval, then display them in the panel
  getUnEnrollmentsStats(baseUrl: string, selectedBrand: string, selectedDateFrom: string, selectedDateTo: string) {
  }

  // Retrieve notifications stats for the given date interval, then display them in the panel
  getNotificationsStats(baseUrl: string, selectedBrand: string, selectedDateFrom: string, selectedDateTo: string) {
  }

  // Retrieve clickthroughs stats for the given date interval, then display them in the panel
  getClickthroughsStats(baseUrl: string, selectedBrand: string, selectedDateFrom: string, selectedDateTo: string) {
    this.isSpinnerHidden = false;
    this.clientStatsService.getClickthroughsTotalCounts(baseUrl, selectedBrand, selectedDateFrom, selectedDateTo)
    .pipe(
      finalize(() => {
        this.isSpinnerHidden = true;
        this.cdr.markForCheck(); // nudge OnPush to re-evaluate `[hidden]`
      })
    )
    .subscribe({
      next: (response) => {
        this.customLoggerService.logDebug('Clickthrough stats response: ', response);
        const idx = this.tiles.findIndex(t => t.key === 'clickthroughs');
        if (idx !== -1) {
          const updated: TileStat = {
            ...this.tiles[idx],
            value: response.firstItem,
            delta: response.thirdItem,
            subtitle: this.tileSubTitle,
            key: 'clickthroughs',    
            icon: 'link',
            title: 'Clickthroughs',        
            description: 'Number of clickthroughs in the ' + this.tileSubTitle.toLocaleLowerCase() + '.' 
          };

          // ❗ Replace array reference (immutable update)
          this.tiles = [
            ...this.tiles.slice(0, idx),
            updated,
            ...this.tiles.slice(idx + 1)
          ];
        }

        this.cdr.markForCheck(); // cause the tile values to repaint
      },
      error: (error) => {
        this.customLoggerService.logError('Error fetching clickthrough stats', error);
      },
      complete: () => {
      }
    });

  }

  // Retrieve notification preference changes stats for the given date interval, then display them in the panel
  getPrefChangesStats(baseUrl: string, selectedBrand: string, selectedDateFrom: string, selectedDateTo: string) {
    this.isSpinnerHidden = false;
    this.clientStatsService.getPrefChangesTotalCounts(baseUrl, selectedBrand, selectedDateFrom, selectedDateTo)
    .pipe(
      finalize(() => {
        this.isSpinnerHidden = true;
        this.cdr.markForCheck(); // nudge OnPush to re-evaluate `[hidden]`
      })
    )
    .subscribe({
      next: (response) => {
        this.customLoggerService.logDebug('Preference changes stats response: ', response);
        const idx = this.tiles.findIndex(t => t.key === 'pref-changes');
        if (idx !== -1) {
          const updated: TileStat = {
            ...this.tiles[idx],
            value: response.firstItem,
            delta: response.thirdItem,
            subtitle: this.tileSubTitle,
            key: 'pref-changes',    
            icon: 'link',
            title: 'Preference Changes',        
            description: 'Number of preference changes in the ' + this.tileSubTitle.toLocaleLowerCase() + '.' 
          };

          // ❗ Replace array reference (immutable update)
          this.tiles = [
            ...this.tiles.slice(0, idx),
            updated,
            ...this.tiles.slice(idx + 1)
          ];
        }

        this.cdr.markForCheck(); // cause the tile values to repaint
      },
      error: (error) => {
        this.customLoggerService.logError('Error fetching clickthrough stats', error);
      },
      complete: () => {
      }
    });

  }

  // Retrieve incomplete enrollments stats for the given date interval, then display them in the panel
  getIncompleteEnrollmentsStats(baseUrl: string, selectedBrand: string, selectedDateFrom: string, selectedDateTo: string) {
    this.isSpinnerHidden = false;
    this.clientStatsService.getIncompleteEnrollmentsTotalCounts(baseUrl, selectedBrand, selectedDateFrom, selectedDateTo)
    .pipe(
      finalize(() => {
        this.isSpinnerHidden = true;
        this.cdr.markForCheck(); // nudge OnPush to re-evaluate `[hidden]`
      })
    )
    .subscribe({
      next: (response) => {
        this.customLoggerService.logDebug('Incomplete enrollments stats response: ', response);
        const idx = this.tiles.findIndex(t => t.key === 'incomplete-enrollments');
        if (idx !== -1) {
          const updated: TileStat = {
            ...this.tiles[idx],
            value: response.firstItem,
            delta: response.thirdItem,
            subtitle: this.tileSubTitle,
            key: 'incomplete-enrollments',    
            icon: 'link',
            title: 'Incomplete Enrollments',        
            description: 'Number of incomplete enrollments in the ' + this.tileSubTitle.toLocaleLowerCase() + '.' 
          };

          // ❗ Replace array reference (immutable update)
          this.tiles = [
            ...this.tiles.slice(0, idx),
            updated,
            ...this.tiles.slice(idx + 1)
          ];
        }

        this.cdr.markForCheck(); // cause the tile values to repaint
      },
      error: (error) => {
        this.customLoggerService.logError('Error fetching clickthrough stats', error);
      },
      complete: () => {
      }
    });

  }

  get isDetailsOpen(): boolean {
    return this.selectedIndex !== null;
  }

  selectTile(idx: number) {
    // Toggle selection
    this.selectedIndex = (this.selectedIndex === idx) ? null : idx;

    if (!this.isDetailsOpen) {
      // Closing: clear any dynamic content
      this._clientStatsVcr?.clear();
      this._activeCmpRef = null;
      this._pendingIndex = null;
      return;
    }

    // Opening or switching
    if (this._clientStatsVcr) {
      // Anchor already available: load immediately
      this.loadDetailsForIndex(idx);
    } else {
      // Anchor not created yet (first open): remember intent
      this._pendingIndex = idx;
    }

  }

  private loadDetailsForIndex(idx: number) {
    const vcr = this._clientStatsVcr;
    if (!vcr) return; // safety

    vcr.clear();
    this._activeCmpRef = null;
        this.customLoggerService.logDebug('loadDetailsForIndex idx: ', idx);
        this.customLoggerService.logDebug('loadDetailsForIndex this.tiles[idx]: ', this.tiles[idx]);

    if(idx !== null && this.tiles[idx] != null) {
      const section = this.clientStatsSectionFactoryService.getComponentSection(this.tiles[idx].key);
      this.customLoggerService.logDebug('loadDetailsForIndex section: ', section);
      this.customLoggerService.logDebug('loadDetailsForIndex section component: ', section.component.name);
      const cmpRef = vcr.createComponent(section.component);
      this._activeCmpRef = cmpRef;
      (cmpRef.instance as any).data = section.data;
      cmpRef.setInput('key', this.tiles[idx].key);
      cmpRef.setInput('params', { 
        brandCode: this.selectedBrand, 
        baseUrl: this.baseUrl, 
        brandName: this.brandName,
        isSamsungPlatformEnabled: this.isSamsungPlatformEnabled,
        dateFrom: this.selectedDateFrom,
        dateTo: this.selectedDateTo 
      });
      // Pass the readonly signal<string> to the child
      // cmpRef.setInput('messageSignal', this._subcomponetMode.asReadonly());   
      cmpRef.setInput('subcomponetModeSignal', this._subcomponetMode.asReadonly());   
      cmpRef.setInput('chartTypeSignal', this._chartType.asReadonly());
      this.sendSubcomponetMode('Summary'); // default to summary when opening details
      this.sendChartType('bar'); // default to bar chart when opening details

    }
  }

  onSetSubcomponetMode(mode: string) {
    this.sendSubcomponetMode(mode); 
  }

  onChartTypeChanged(data: any) {
    this.sendChartType(this.selectedChartType); 
  }  

  closeDetails() {
    this.selectedIndex = null;
    this._clientStatsVcr?.clear();
    this._activeCmpRef = null;
    this._pendingIndex = null;
  }

  trackByKey = (_: number, t: TileStat) => t.key;
}
